<script>
import axios from '@/utils/axios';
import { allUsers } from '@/utils/user';
import { useToast } from 'vue-toastification';

export default {
 inject: ['setLoading'],
 data() {
   return {
    title: "CRM",
    text: "CRM", 
    text1: "Import CSV",
    toast: useToast(),
    selectedFile: null,
    dragActive: false,
    uploading: false,
    users: [],
    user_id: '',
    imports: [],
    pagination: {
     current_page: 1,
     last_page: 1,
     per_page: 10,
     total: 0
    },
    import_type: 'leads',
    loadingImports: false
   }
  },

  mounted(){
   this.getUsers(); 
   this.fetchImports();
  },

  methods: {

   formatFieldName(fieldName) {
    const fieldMap = {
    'mobile': 'Mobile',
    'email': 'Email',
    'name': 'Name',
    'stage': 'Stage',
    'source': 'Source',
    'country_code': 'Country',
    'assign_to': 'Assign To'
    };
    return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
   },  

   async getUsers(){
    try{
     const response = await allUsers();
     this.users = response.data.data;
     if(!this.id){
      this.setDefaultLeadUser(); 
     }
    }catch(error){
     console.log(error.message);
    } 
   },

   // Handle file selection via input
   handleFileSelect(event) {
    const file = event.target.files[0];
    this.validateAndSetFile(file);
   },
    
    // Handle drag and drop events
   handleDragOver(event) {
    event.preventDefault();
    this.dragActive = true;
   },
    
   handleDragLeave(event) {
    event.preventDefault();
    this.dragActive = false;
   },
    
   handleDrop(event) {
    event.preventDefault();
    this.dragActive = false;  
    const files = event.dataTransfer.files;
    if(files.length > 0){
     this.validateAndSetFile(files[0]);
    }
   },
    
   // Validate file type and size
   validateAndSetFile(file){
    if(!file) return;
    // Check if it's a CSV file
    if(file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')){
     this.$swalToast('Please select a valid CSV file', 'error');
     return;
    }
      
    // Check file size (max 5MB)
    if(file.size > 5 * 1024 * 1024){
     this.$swalToast('File size should be less than 5MB', 'error');
     return;
    }  
     this.selectedFile = file;
   },
    
   // Upload file to API
   async uploadFile() {
    if(!this.selectedFile){
     this.$swalToast('Please select a CSV file first', 'error');
     return;
    }
      
    this.uploading = true;
    this.setLoading(true);
      
    try{
     // Create FormData object
     const formData = new FormData();
     formData.append('file', this.selectedFile);
     formData.append('import_type', 'leads');
     formData.append('user_id', this.user_id);
        
     const response = await axios.post('/csv/import', formData, {
       headers: {
        'Content-Type': 'multipart/form-data'
       }
     });
        
     // Handle success
     this.$swalToast(response.data.message || 'CSV imported successfully!');
     this.selectedFile = null;
        
     // Reset file input
     this.$refs.fileInput.value = '';
     this.fetchImports();
    }catch(error){
     if(error.type === 'validation') {
      this.errors = error.errors;
     }else{
      this.$swalToast('Failed to import CSV file. Please try again.', 'error');
     }
    }finally{
     this.uploading = false;
     this.setLoading(false);
    }
   },
    
    // Remove selected file
   removeFile() {
    this.selectedFile = null;
    this.$refs.fileInput.value = '';
   },

   // Fetch import history
    async fetchImports(page = 1) {
     this.loadingImports = true; 
     try{
      const params = {
       page,
       import_type: this.import_type,
       per_page: this.pagination.per_page 
      };  
      const query_string = new URLSearchParams(params).toString();
      const response = await axios.get(`/csv/imports?${query_string}`);
        
      this.imports = response.data.data.data;
      this.pagination = {
       current_page: response.data.data.current_page,
       last_page: response.data.data.last_page,
       per_page: response.data.data.per_page,
       total: response.data.data.total
      };
     }catch(error){
      this.$swalToast(error.message, 'error');
     }finally{
      this.loadingImports = false;
     }
    },

    // Get status badge class
    getStatusClass(status){
     const statusClasses = {
       'queued': 'bg-warning',
       'processing': 'bg-info',
       'completed': 'bg-success',
       'failed': 'bg-danger'
     };
     return statusClasses[status] || 'bg-secondary';
    },
    
    // Get progress bar class
    getProgressClass(status){
     if (status === 'completed') return 'bg-success';
     if (status === 'failed') return 'bg-danger';
     if (status === 'processing') return 'bg-info';
     return 'bg-warning';
    },
    
    // Change page
    changePage(page){
     if(page >= 1 && page <= this.pagination.last_page && page !== this.pagination.current_page){
      this.fetchImports(page);
     }
    },

    downloadSample(){
     try{
      // Create download link
      const link = document.createElement('a');
      link.href = '/samples/import-leads.csv';
      link.download = 'leads-import-sample.csv';
      link.target = '_blank';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
     }catch(error){
      console.error('Download error:', error);
      this.$swalToast('Failed to download sample CSV', 'error');
     }
  }

  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  
  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="">
          <router-link :to="{name: 'leads-list'}" class="btn btn-primary btn-sm">
            <i class="ti ti-chevrons-left"></i> Leads
          </router-link>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="card" style="background-color:#7367f01f;">
        <div class="card-body pb-1">
          <div class="mb-3">
            <p>
              Import your existing client database and start engaging with your clients immediately. 
              To begin, download the <button type="button" class="fw-bold btn btn-primary btn-sm" @click="downloadSample">Lead Import Template</button> update it with your client details, and upload the CSV file. 
              Please review the <a href="javascript:void(0);" class="fw-bold" data-bs-toggle="modal" data-bs-target="#import_format_modal">Lead Import format guidelines</a> for accurate setup
            </p>
          </div>   
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="card-title">CSV File Upload</h5>
        </div>
        <div class="card-body">
        <p class="text-muted">Upload your CSV file according to given format.</p>
          
          <!-- File Drop Zone -->
        <div class="dropzone-area" :class="{ 'drag-active': dragActive }" @dragover="handleDragOver" @dragleave="handleDragLeave" @drop="handleDrop" @click="$refs.fileInput.click()">
        <input type="file" ref="fileInput" accept=".csv" style="display: none;" @change="handleFileSelect"/>
            
        <div class="dz-message text-center py-5">
        <i class="ti ti-cloud-upload h1 text-muted"></i>
        <h3 v-if="!selectedFile">Drop CSV file here or click to upload</h3>
        <div v-else class="selected-file-info">
        <i class="ti ti-file-text h2 text-success"></i>
        <h5 class="text-success mt-2">{{ selectedFile.name }}</h5>
        <p class="text-muted">{{ (selectedFile.size / 1024).toFixed(2) }} KB</p>
        <button type="button" class="btn btn-sm btn-outline-danger mt-2" @click.stop="removeFile">
         <i class="ti ti-trash"></i> Remove</button>
        </div>
        </div>
        </div>
          
        <!-- Upload Button -->
        <div class="text-center mt-4" v-if="selectedFile">

         <vue-select :options="users" label="name" placeholder="Assign To" v-model="user_id" :reduce="u => u.id" /> 

         <button type="button" class="btn btn-success btn-lg" :disabled="uploading" @click="uploadFile">
          <span v-if="uploading">
           <i class="spinner-border spinner-border-sm me-2"></i>Importing...
          </span>
          <span v-else>
           <i class="ti ti-upload me-2"></i>Import CSV
           </span>
          </button>
         </div>
        </div>
       </div>

     <!-- Import History -->
      <div class="card">
       <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="card-title mb-0">Import History</h5>
        <button type="button" class="btn btn-outline-primary btn-sm" @click="fetchImports()" :disabled="loadingImports">
        <i class="ti ti-refresh" :class="{ 'spin': loadingImports }"></i> Refresh
        </button>
        </div>
        <div class="card-body">
          
        <!-- Loading State -->
        <div v-if="loadingImports" class="text-center py-4">
         <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
         </div>
         <p class="mt-2 text-muted">Loading import history...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="imports.length === 0" class="text-center py-5">
         <i class="ti ti-file-import h1 text-muted"></i>
         <h5 class="text-muted">No imports found</h5>
         <p class="text-muted">Upload your first CSV file to see import history</p>
        </div>

        <!-- Import History Table -->
        <div v-else class="table-responsive">
         <table class="table table-hover">
          <thead class="table-light">
           <tr>
            <th>File Name</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Rows</th>
            <th>Success/Failed</th>
            <th>Started</th>
            <th>Completed</th>
            <th>Actions</th>
           </tr>
           </thead>
           <tbody>
            <tr v-for="importItem in imports" :key="importItem.id">
            <td>
             <div class="d-flex align-items-center">
              <i class="ti ti-file-csv me-2 text-success"></i>
             <div>
             <div class="fw-medium">{{ importItem.file_name }}</div>
             <small class="text-muted">ID: {{ importItem.import_id.substring(0, 8) }}...</small>
             </div>
             </div>
             </td>
             <td>
              <span class="badge" :class="getStatusClass(importItem.status)"> {{ importItem.status }} </span>
             </td>
             <td>
             <div class="progress" style="height: 8px; width: 80px;">
              <div class="progress-bar" :class="getProgressClass(importItem.status)" :style="{ width: importItem.progress_percentage + '%' }"></div>
             </div>
             <small class="text-muted">{{ importItem.progress_percentage }}%</small>
             </td>
             <td>
              <div class="text-center">
               <div class="fw-medium">{{ importItem.total_rows }}</div>
               <small class="text-muted">Total</small>
              </div>
             </td>
             <td>
             <div class="d-flex gap-2">
              <span class="badge bg-success-light text-success" v-if="importItem.successful_rows > 0">
               {{ importItem.successful_rows }} ✓
              </span>
              <span class="badge bg-danger-light text-danger" v-if="importItem.failed_rows > 0">
               {{ importItem.failed_rows }} ✗
              </span>
             </div>
             </td>
             <td>
              <small>{{ importItem.started_at ? $formatDate(importItem.started_at) : '' }}</small>
             </td>
             <td>
              <small>{{ importItem.completed_at ? $formatDate(importItem.completed_at) : '' }}</small>
             </td>
             <td>
              <div class="d-flex gap-1">
              <!-- View Errors Button -->
              <button v-if="importItem.errors && importItem.errors.length > 0" type="button" class="btn btn-outline-danger btn-sm" data-bs-toggle="modal" :data-bs-target="`#errorsModal${importItem.id}`">
                <i class="ti ti-alert-circle"></i>
              </button>
              </div>
             </td>
            </tr>
           </tbody>
           </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.last_page > 1" class="d-flex justify-content-between align-items-center mt-4">
            <div class="text-muted">
              Showing {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} to 
              {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} of 
              {{ pagination.total }} entries
            </div>

           <nav aria-label="Import history pagination">
            <ul class="pagination pagination-sm mb-0">
             <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
              <button class="page-link" @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1">Previous</button>
             </li>
                
             <li v-for="page in Math.min(pagination.last_page, 5)" :key="page" class="page-item" :class="{ active: page === pagination.current_page }">
              <button class="page-link" @click="changePage(page)">{{ page }}</button>
             </li>
                
             <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
              <button class="page-link" @click="changePage(pagination.current_page + 1)" :disabled="pagination.current_page === pagination.last_page">Next
              </button>
             </li>
             </ul>
            </nav>

          </div>
          </div>
          </div>

      <!-- Error Modals -->
      <div v-for="importItem in imports" :key="`modal-${importItem.id}`" class="modal fade" 
      :id="`errorsModal${importItem.id}`" tabindex="-1">
      <div class="modal-dialog modal-lg">
      <div class="modal-content">
      <div class="modal-header">
      <h5 class="modal-title">Import Errors - {{ importItem.file_name }}</h5>
      <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
      <div v-if="importItem.errors && importItem.errors.length > 0">
        <div v-for="(errorItem, index) in importItem.errors" :key="index" class="alert alert-danger mb-3">
          <div class="fw-bold mb-2">Row {{ errorItem.row }}:</div>
          
          <!-- Single error string -->
          <div v-if="errorItem.error" class="mb-3 p-3 bg-danger-subtle border border-danger rounded-1">
            <div class="text-danger">{{ errorItem.error }}</div>
          </div>
          
          <!-- Nested errors object -->
          <div v-else-if="errorItem.errors" class="mb-3">
            <div v-for="(fieldErrors, fieldName) in errorItem.errors" :key="fieldName" class="mb-2">
              <strong class="text-danger">{{ formatFieldName(fieldName) }}:</strong>
              <ul class="mb-0 ms-3">
                <li v-for="(errorMsg, msgIndex) in fieldErrors" :key="msgIndex" class="small">
                  {{ errorMsg }}
                </li>
              </ul>
            </div>
          </div>
          
          <!-- Row data always available -->
          <details class="mt-3" v-if="errorItem.data">
            <summary class="text-muted cursor-pointer small fw-medium">View Row Data</summary>
            <pre class="mt-2 small bg-light p-3 rounded overflow-auto" style="max-height: 250px; font-size: 0.8em;">
      {{ JSON.stringify(errorItem.data, null, 2) }}
            </pre>
          </details>
        </div>
      </div>
      <div v-else class="text-center text-muted py-4">
        <i class="ti ti-check-circle h1 text-success"></i>
        <p>No errors found in this import.</p>
      </div>
      </div>
      <div class="modal-footer">
      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
      </div>
      </div>
      </div>
      </div>
      
      <!-- IMPORT FORMAT GUIDELINE MODAL -->
      <div class="modal" id="import_format_modal">
      <div class="modal-dialog modal-dialog-scrollable">
      <div class="modal-content rounded-0">

      <!-- Modal Header -->
      <div class="modal-header">
        <h4 class="modal-title">Import Format Guideline</h4>
        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
      </div>

      <!-- Modal body -->
       <div class="modal-body p-0">
        <div class="card">
         <div class="card-header">
          <h4 class="text-primary"># Required Fields:</h4>
         </div>   
         <div class="card-body p-0">
          <table cellpadding="10">
         <tr>
          <td class="fw-bold">Name </td>
          <td>Client’s full name (cannot be left blank).</td>  
         </tr>
         <tr>
          <td class="fw-bold">Mobile </td>
          <td>Client’s mobile no (cannot be left blank).</td>  
         </tr> 
         <tr>
          <td class="fw-bold">Stage </td>
          <td>Lead stage cannot be left blank.</td>  
         </tr>
         <tr>
          <td class="fw-bold">Source </td>
          <td>Source of lead cannot be left blank.</td>  
         </tr> 
         <tr>
          <td class="fw-bold">Country </td>
          <td>A valid 2-letter country code (e.g., US, IN, UK).</td>  
         </tr>     
        </table>
         </div>   
        </div>
        
        <div class="card">
         <div class="card-header">
          <h4 class="text-primary"># Validation Rules:</h4>
         </div>
         <div class="card-body p-0">
          <table cellpadding="10">
         <tr>
          <td class="fw-bold">Email </td>
          <td>Must follow a valid format (e.g., user@domain.com).</td>  
         </tr>
         <tr>
          <td class="fw-bold">Mobile </td>
          <td>Do not include the country code.</td>  
         </tr> 
         <tr>
          <td class="fw-bold">Formatting </td>
          <td>Use the exact column names as provided in the CSV template.</td>  
         </tr>  
        </table> 
         </div>   
        </div>

       </div>

       </div>
      </div>
     </div> 


    </div>

    <app-footer></app-footer>
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
.dropzone-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
}

.dropzone-area:hover {
  border-color: #007bff;
  background: #f8f9ff;
}

.dropzone-area.drag-active {
  border-color: #28a745;
  background: #f8fff9;
  transform: scale(1.02);
}

.selected-file-info {
  padding: 20px;
}

.dz-message h3 {
  color: #6c757d;
  font-weight: 300;
}
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.cursor-pointer {
  cursor: pointer;
}

.bg-success-light {
  background-color: #d4edda !important;
}

.bg-danger-light {
  background-color: #f8d7da !important;
}
</style>
